import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Row, Col, Button } from "reactstrap";
import { toast } from "react-toastify";
import DataTable from "./components/Datatable";
import BranchUserFormModal from "./components/Branchuserformmodal";
import DeleteConfirmModal from "./components/Deleteconfirmmodal";
import axiosInstance from "Axiosinstance";


const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const BranchUser = () => {
  const [company, setCompany] = useState(null);
  const [branchUsers, setBranchUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search + pagination. The `/api/branchuser/:uid` endpoint returns the
  // full list in one go (no server-side search/page params), so we paginate
  // and filter on the client but still drive the reusable DataTable the
  // same way a backend-paginated table would be driven.
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // Create/edit modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [editingUser, setEditingUser] = useState(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadBranchUsers();
  }, []);

  const loadBranchUsers = async () => {
    setLoading(true);
    try {
      const user = localStorage.getItem("user");
      const parsedUser = user ? JSON.parse(user) : null;
      const uid = parsedUser?.uid;

      if (!uid) {
        toast.error("Unable to determine logged in user.");
        setLoading(false);
        return;
      }

      const { data } = await axiosInstance.get(`/api/branchuser/${uid}`);
      if (data?.status) {
        setBranchUsers(data.data || []);
        setCompany(data.company || null);
      } else {
        toast.error(data?.message || data?.msg || "Unable to fetch branch users.");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Unable to load branch users."
      );
    } finally {
      setLoading(false);
    }
  };

  const getBranchLabel = (branchUid) => {
    const branch = company?.branches?.find(
      (b) => b.branch_uid === branchUid || b._id?.toString() === branchUid
    );
    return branch?.branch_name || "Unknown Branch";
  };

  // ---- Search + pagination (client-side) ----
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return branchUsers;
    return branchUsers.filter((u) => {
      const haystack = [
        u.first_name,
        u.last_name,
        u.email,
        u.phone,
        getBranchLabel(u.branch_uid),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [branchUsers, searchTerm, company]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, page]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    setPage(Math.min(Math.max(nextPage, 1), totalPages));
  };

  // ---- Create / Edit ----
  const openCreateModal = () => {
    setFormMode("create");
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const openEditModal = (user) => {
    setFormMode("edit");
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const toggleFormModal = () => setIsFormOpen((prev) => !prev);

  // ---- Delete ----
  const openDeleteConfirm = (user) => setDeleteTarget(user);
  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  // NOTE: assumes `DELETE /api/branchuser/:uid`. Adjust if your backend
  // uses a different route/method (e.g. a soft-delete POST endpoint).
  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await axiosInstance.delete(
        `/api/branchuser/${deleteTarget.uid}`
      );
      if (data?.status) {
        toast.success(data?.message || data?.msg || "Branch user deleted successfully");
        setDeleteTarget(null);
        loadBranchUsers();
      } else {
        toast.error(data?.message || data?.msg || "Unable to delete branch user");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Unable to delete branch user"
      );
    } finally {
      setDeleting(false);
    }
  };

  // ---- Table columns ----
  const columns = [
    {
      dataField: "first_name",
      text: "Name",
      formatter: (_, row) => `${row.first_name || ""} ${row.last_name || ""}`.trim(),
    },
    {
      dataField: "email",
      text: "Email",
    },
    {
      dataField: "phone",
      text: "Phone",
      formatter: (_, row) => `${row.phonecountrycode || ""} ${row.phone || ""}`.trim(),
    },
    {
      dataField: "branch_uid",
      text: "Branch",
      formatter: (_, row) => getBranchLabel(row.branch_uid),
    },
    {
      dataField: "date",
      text: "Created Date",
      formatter: (_, row) => formatDate(row.date || row.createdate),
    },
    {
      dataField: "updatedate",
      text: "Updated Date",
      formatter: (_, row) => formatDate(row.modified_date || row.modified_date),
    },
    {
      dataField: "actions",
      text: "Actions",
      isDummyField: true,
      formatter: (_, row) => (
        <div className="d-flex gap-3">
          <i
            className="mdi mdi-pencil font-size-18 text-success"
            role="button"
            title="Edit"
            onClick={() => openEditModal(row)}
          />
          <i
            className="mdi mdi-trash-can-outline font-size-18 text-danger"
            role="button"
            title="Delete"
            onClick={() => openDeleteConfirm(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Card>
        <CardBody>
          <Row className="mb-3 align-items-center">
            <Col>
              <h4 className="card-title mb-0">Branch Users</h4>
              <div className="text-muted">
                {company ? `Company: ${company.company_name}` : "Loading company..."}
              </div>
            </Col>
            <Col className="text-end">
              <Button
                color="primary"
                className="font-16 btn btn-primary px-4 shadow-sm"
                onClick={openCreateModal}
                style={{ borderRadius: "999px", minWidth: "160px" }}
                disabled={!company}
              >
                Create Branch User
              </Button>
            </Col>
          </Row>

          <DataTable
            keyField="uid"
            columns={columns}
            data={pagedUsers}
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            loading={loading}
            searchPlaceholder="Search by name, email, phone, branch..."
          />

          <BranchUserFormModal
            isOpen={isFormOpen}
            toggle={toggleFormModal}
            mode={formMode}
            initialData={editingUser}
            company={company}
            onSuccess={loadBranchUsers}
          />

          <DeleteConfirmModal
            isOpen={!!deleteTarget}
            toggle={closeDeleteConfirm}
            onConfirm={handleDeleteConfirmed}
            loading={deleting}
            title="Delete Branch User"
            message={
              deleteTarget
                ? `Are you sure you want to delete ${deleteTarget.first_name} ${deleteTarget.last_name}? This action cannot be undone.`
                : ""
            }
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default BranchUser;